/* eslint-disable no-undef */
import * as Sentry from '@sentry/react';
import anime from 'animejs/lib/anime.es';
import _ from 'lodash';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import FadeIn from 'react-fade-in';
import { createUseStyles } from 'react-jss';
import MetaTags from 'react-meta-tags';
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useCategoryAPI } from '../../apis/category-api';
import { useItemAPI } from '../../apis/item-api';
import { useMenuAPI } from '../../apis/menu-api';
import { useQRCodeAPI } from '../../apis/qrcode-api';
import { useSpotAPI } from '../../apis/spot-api';
import { LogoAnimated } from '../../components/load/logo-animated';
import { usePageview } from '../../hooks/use-pageview';
import { useStorage } from '../../hooks/use-storage';
import { layoutDefaults } from '../../utils/layout-defaults';
import {
  parseDefaultBackgroundColor,
  parseDefaultTextColor,
} from '../../utils/manager/layout-manager';
import {
  menuLoadingDelay,
  useMenuManager,
} from '../../utils/manager/menu-manager';
import { useI18nSetup } from '../../utils/setup/i18n-setup';
import { wait } from '../../utils/wait';
import { BillScreen } from '../bill/bill-screen';
import { EmptyQRCodeScreen } from '../empty/empty-qrcode-screen';
import { MenuScreen } from '../menu/menu-screen';
import { Error } from './error';
import { NotFound } from './not-found';
import { Feature, useFeature } from '../../hooks/use-feature';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function LoadingMenuScreen() {
  const {
    menuSlug: menuSlugParam,
    menuId: menuIdParam,
    spotId: spotIdParam,
    qrCodeId: qrCodeIdParam,
    categoryId: categoryIdParam,
    itemId: itemIdParam,
  } = useParams();
  const [loading, setLoading] = useState(true);
  const [found, setFound] = useState(false);
  const [failed, setFailed] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [batch, setBatch] = useState();
  const {
    loadStorage,
    menuLayout,
    menuSlug,
    menuInfo,
    liveEditMode,
    setMenuSlug,
    setLoadedSpot,
    setLoadedCategory,
    setLoadedItem,
    setLiveEditMode,
    languages,
  } = useStorage();
  const { loadMenu } = useMenuManager();
  const { loadSpotFromRemote } = useSpotAPI();
  const { loadQRCodeFromRemote } = useQRCodeAPI();
  const { loadCategoryFromRemote } = useCategoryAPI();
  const { loadItemFromRemote } = useItemAPI();
  const { loadMenuFromRemote } = useMenuAPI();
  const { isFeatureEnabled } = useFeature();
  const navigate = useNavigate();
  const classes = useStyles(menuLayout);
  const isSettingUpLayoutRef = useRef(false);
  const [searchParams] = useSearchParams();
  const loadingRef = useRef(false);

  usePageview('Loading');
  useI18nSetup();

  useEffect(() => {
    console.log(`searchParams: live`);
    if (liveEditMode == null) {
      const live = searchParams.get('live') === 'true';
      setLiveEditMode(live);
    }
    // }
  }, [searchParams]);

  useLayoutEffect(() => {
    if (spotIdParam != null && menuSlug == null) {
      loadSpot();
    }
  }, [spotIdParam, menuSlug]);

  useLayoutEffect(() => {
    if (qrCodeIdParam != null && menuSlug == null) {
      loadQRCode();
    }
  }, [qrCodeIdParam, menuSlug]);

  useLayoutEffect(() => {
    if (categoryIdParam != null && menuSlug == null) {
      loadCategory();
    }
  }, [categoryIdParam, menuSlug]);

  useLayoutEffect(() => {
    if (itemIdParam != null && menuSlug == null) {
      loadItem();
    }
  }, [itemIdParam, menuSlug]);

  useLayoutEffect(() => {
    if (menuIdParam != null && menuSlug == null) {
      loadMenuOnly();
    }
  }, [menuIdParam, menuSlug]);

  useEffect(() => {
    if (!_.isEmpty(menuSlugParam)) {
      setMenuSlug(menuSlugParam);
    }
  }, [menuSlugParam]);

  useLayoutEffect(() => {
    if (!_.isEmpty(menuSlug) && liveEditMode != null) {
      loadMenuAndUserOrders();
    }
  }, [menuSlug, liveEditMode]);

  useEffect(() => {
    if (!_.isEmpty(menuLayout)) {
      setupLayout();
    }
  }, [menuLayout, found]);

  async function loadQRCode() {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      const response = await loadQRCodeFromRemote(qrCodeIdParam);
      const rQRCode = response.data;

      if (rQRCode.spot == null) {
        setBatch(rQRCode.batch);

        setEmpty(true);
        setLoading(false);
      } else if (rQRCode.batch?.sales === true) {
        // TODO Cartão de visita
        console.log('Cartão de visita');
      } else {
        await loadSpotLogic(rQRCode.spot);
      }
    } catch (error) {
      handleParamLoadingError(error);
    }

    loadingRef.current = false;
  }

  async function loadSpot() {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      const response = await loadSpotFromRemote(spotIdParam);
      const rSpot = response.data;

      await loadSpotLogic(rSpot);
    } catch (error) {
      handleParamLoadingError(error);
    }

    loadingRef.current = false;
  }

  async function loadSpotLogic(pSpot) {
    if (pSpot.menu?.info?.slug != null) {
      const { slug: lSlug } = pSpot.menu.info;

      // if (pSpot.hidden) {
      //   setLoadedSpot(null);
      // } else {
      delete pSpot.menu;
      setLoadedSpot(pSpot);
      // }

      navigate(`/${lSlug}`);
    } else {
      await wait(menuLoadingDelay);
      setLoading(false);
    }
  }

  async function loadItem() {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      const response = await loadItemFromRemote(itemIdParam);
      const rItem = response.data;

      const { slug: lSlug } = rItem.menu.info;

      setLoadedItem(rItem);
      navigate(`/${lSlug}`);
    } catch (error) {
      handleParamLoadingError(error);
    }

    loadingRef.current = false;
  }

  async function loadCategory() {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      const response = await loadCategoryFromRemote(categoryIdParam);
      const rCategory = response.data;
      const { slug: lSlug } = rCategory.menu.info;

      setLoadedCategory(rCategory);
      navigate(`/${lSlug}`);
    } catch (error) {
      handleParamLoadingError(error);
    }

    loadingRef.current = false;
  }

  async function loadMenuOnly() {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      const response = await loadMenuFromRemote(menuIdParam);
      const rMenu = response.data;
      const { slug: lSlug } = rMenu.info;

      navigate(`/${lSlug}`);
    } catch (error) {
      handleParamLoadingError(error);
    }

    loadingRef.current = false;
  }

  async function handleParamLoadingError(error) {
    console.error(error);

    await wait(menuLoadingDelay);
    if (error.response?.status === 404) {
      setFound(false);
    } else {
      setFailed(true);
    }

    setLoading(false);
  }

  async function loadMenuAndUserOrders() {
    loadStorage();

    try {
      await loadMenu({
        includesOrders: true,
        includesLanguages: true,
        includesImages: true,
        includesFonts: true,
        shouldWait: true,
      });

      setFound(true);
    } catch (error) {
      console.error(error);

      if (error?.response?.status === 404) {
        setFailed(false);
      } else {
        setFailed(true);
      }
    }

    setLoading(false);
  }

  function setupLayout() {
    if (isSettingUpLayoutRef.current) return;
    isSettingUpLayoutRef.current = true;

    const layout = menuLayout;
    // anime({
    //   targets: `body`,
    //   backgroundColor:
    //     layout != null
    //       ? parseDefaultBackgroundColor(layout)
    //       : layoutDefaults.primaryColor,
    //   easing: 'easeInOutQuad',
    //   duration: 400,
    // });

    if (layout != null && found) {
      anime({
        targets: `body`,
        color: parseDefaultTextColor(layout),
        backgroundColor: layout.primaryColor,
        duration: 0,
      });

      anime({
        targets: `meta[name='theme-color']`,
        content: parseDefaultBackgroundColor(layout),
        duration: 1000,
        delay: 0,
      });
    }

    // anime({
    //   targets: `html`,
    //   backgroundColor:
    //     layout != null
    //       ? parseContainerBackgroundShade(layout)
    //       : layoutDefaults.primaryColor,
    //   duration: 0,
    // });

    isSettingUpLayoutRef.current = false;
  }

  if (loading) {
    return (
      <div className={classes.container}>
        <MetaTags>
          <title>{menuInfo?.name ?? 'Menur'}</title>
          <meta property="og:title" content={menuInfo?.name ?? 'Menur'} />
          <meta name="description" content={menuInfo?.description} />
          <meta name="og:description" content={menuInfo?.description} />
          <meta name="og:type" content="website" />
          <meta property="og:image" content={menuLayout?.logo?.url} />
          <meta
            property="og:locale"
            content={menuInfo?.i18n?.default_language}
          />
          {isFeatureEnabled(Feature.TRANSLATION) &&
            languages
              ?.filter((l) => l.tag !== menuInfo?.i18n?.default_language?.tag)
              ?.map((language) => (
                <meta property="og:locale" content={language.tag} />
              ))}
          <meta property="og:url" content={window.location.href} />
          {/* <meta property="og:url" content={`${menuSlug}`} /> */}
        </MetaTags>
        <FadeIn>
          <LogoAnimated className={classes.logo} layout={menuLayout} />
        </FadeIn>
      </div>
    );
  }

  if (empty) {
    return <EmptyQRCodeScreen id={qrCodeIdParam} batch={batch} />;
  }

  if (found) {
    return (
      <SentryRoutes>
        <Route path="conta" exact element={<BillScreen />} />
        <Route path=":toBeIgnored" element={<MenuScreen />} />
        <Route path="" exact element={<MenuScreen />} />
      </SentryRoutes>
    );
  }

  if (failed) {
    return <Error />;
  }

  return <NotFound className={classes.logo} />;
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    minHeight: '100vh',
    backgroundColor: layoutDefaults.primaryColor,

    paddingBottom: '10vh',

    // paddingBottom:
    // border: "1px solid red",
  },
  logo: {
    touchAction: 'pan-x pan-y',
  },
});

export { LoadingMenuScreen };
